/**
 * Мок тарифов. Активные версии собираются из констант расчёта
 * (`@services/clients` — единый источник), плюс прошлые версии для истории.
 * Числа синхронны дизайну Tariffs (соло v4, клиника v7). Мутации мока
 * (`applySoloVersionMock`/`applyClinicVersionMock`) двигают версию и историю,
 * чтобы после инвалидации UI вёл себя как с живым BE.
 */

import {CLINIC_OVER_PRICE, CLINIC_PACKAGES, SOLO_PRICE} from '@services/clients';
import {formatNumber} from '@utils/format';

import type {ClinicTariff, SaveClinicInput, SoloTariff, TariffHistoryItem} from './types';

export const CURRENT_SOLO: SoloTariff = {
  version: 4,
  updatedAt: '2026-06-12',
  price: SOLO_PRICE,
};

export const CURRENT_CLINIC: ClinicTariff = {
  version: 7,
  updatedAt: '2026-05-28',
  packages: CLINIC_PACKAGES.map((p) => ({...p})),
  overPrice: CLINIC_OVER_PRICE,
};

export const TARIFF_HISTORY: TariffHistoryItem[] = [
  {
    id: 'clinic-v7',
    kind: 'clinic',
    version: 7,
    date: '2026-05-28',
    current: true,
    summary: 'до 3: 3 000 · до 6: 5 000 · до 10: 8 000 · 11+: 800/место',
  },
  {
    id: 'clinic-v6',
    kind: 'clinic',
    version: 6,
    date: '2026-04-02',
    current: false,
    summary: 'до 3: 3 000 · до 6: 5 500 · до 10: 8 500 · 11+: 850/место',
  },
  {
    id: 'solo-v4',
    kind: 'solo',
    version: 4,
    date: '2026-06-12',
    current: true,
    summary: '1 200 сом / мес',
  },
];

/** Сколько клиентов затронет смена версии (для модалки подтверждения). */
export const AFFECTED_ON_SAVE: Record<'solo' | 'clinic', number> = {
  solo: 138,
  clinic: 54,
};

const pushHistory = (item: TariffHistoryItem) => {
  TARIFF_HISTORY.forEach((h) => {
    if (h.kind === item.kind) h.current = false;
  });
  TARIFF_HISTORY.unshift(item);
};

/** Мок-сохранение соло: bump версии + строка истории. Возвращает новую версию. */
export const applySoloVersionMock = (price: number): number => {
  CURRENT_SOLO.version += 1;
  CURRENT_SOLO.price = price;
  CURRENT_SOLO.updatedAt = new Date().toISOString();
  pushHistory({
    id: `solo-v${CURRENT_SOLO.version}`,
    kind: 'solo',
    version: CURRENT_SOLO.version,
    date: CURRENT_SOLO.updatedAt,
    current: true,
    summary: `${formatNumber(price)} сом / мес`,
  });
  return CURRENT_SOLO.version;
};

/** Мок-сохранение клиники: пакеты + overPrice + история. Возвращает версию. */
export const applyClinicVersionMock = (input: SaveClinicInput): number => {
  CURRENT_CLINIC.version += 1;
  CURRENT_CLINIC.packages = input.packages.map((p) => ({...p}));
  CURRENT_CLINIC.overPrice = input.overPrice;
  CURRENT_CLINIC.updatedAt = new Date().toISOString();
  const last = input.packages[input.packages.length - 1];
  pushHistory({
    id: `clinic-v${CURRENT_CLINIC.version}`,
    kind: 'clinic',
    version: CURRENT_CLINIC.version,
    date: CURRENT_CLINIC.updatedAt,
    current: true,
    summary: [
      ...input.packages.map((p) => `до ${p.upTo}: ${formatNumber(p.price)}`),
      `${last.upTo + 1}+: ${formatNumber(input.overPrice)}/место`,
    ].join(' · '),
  });
  return CURRENT_CLINIC.version;
};
